interface ChainConfig {
  rpc: string | undefined;
  id: number;
  hexId: string;
  network: string | undefined;
  token: string | undefined;
  name: string | undefined;
  scan: string | undefined;
}

interface AlchemyConfig {
  network: string | undefined;
  apiKey: string | undefined;
}

interface CurrencyConfig {
  name: string;
  address: string;
  decimals: number;
}

interface FaucetConfig {
  address: string | undefined;
  decimals: number;
}

interface ApiRetryConfig {
  timeout: number;
  count: number;
}

interface ApiConfig {
  url: string | undefined;
  clock: string | number;
  clockCount: number;
  retry: ApiRetryConfig;
  defaultMaxAge: number;
}

interface EnvironmentConfig {
  name: string | undefined;
  testnet: boolean;
  version: string;
}

interface EthereumRetryConfig {
  timeout: number;
  count: number;
  transactCount: number;
}

interface EthereumConfig {
  retry: EthereumRetryConfig;
  clock: string | number;
  clockCount: number;
  defaultMaxAge: number;
}

interface RefreshConfig {
  default: number;
  userInformation: number;
}

interface PoolConfig {
  address: string | undefined;
}

interface ImagesConfig {
  defaultEtkLogo: string;
}

interface MinichartsConfig {
  daysFrom: number;
}

interface CashFlowChartsConfig {
  daysFrom: number;
}

interface GasConfig {
  increase: number;
}

interface RollbarConfig {
  accessToken: string;
  environment: string | undefined;
  enabled: boolean;
}

interface TrackConfig {
  name: string;
}

interface ApyConfig {
  range: number;
  maxAPY: number;
}

interface WalletConnectConfig {
  projectId: string | undefined;
}

interface KycRiskConfig {
  url: string | undefined;
  hash: string | undefined;
  last_updated: string | undefined;
}

interface KycConfig {
  risk: KycRiskConfig;
}

interface DocsConfig {
  fees: {
    url: string | undefined;
  };
  eTokenRisks: {
    url: string | undefined;
  };
}

interface KycIframeConfig {
  url: string | undefined;
  allowAnyOrigin: boolean;
}

interface Config {
  chain: ChainConfig;
  alchemy: AlchemyConfig;
  currency: CurrencyConfig;
  faucet: FaucetConfig;
  api: ApiConfig;
  environment: EnvironmentConfig;
  ethereum: EthereumConfig;
  refresh: RefreshConfig;
  pool: PoolConfig;
  images: ImagesConfig;
  minicharts: MinichartsConfig;
  cashFlowCharts: CashFlowChartsConfig;
  gas: GasConfig;
  rollbar: RollbarConfig;
  track: TrackConfig;
  apy: ApyConfig;
  apy360: ApyConfig;
  walletConnect: WalletConnectConfig;
  overrides: string[];
  kyc: KycConfig;
  docs: DocsConfig;
  kycIframe: KycIframeConfig;
}

function toList(envVar: string | undefined): string[] {
  if (envVar === undefined) return [];
  if (envVar === "") return [];
  return envVar.split(",");
}

const config: Config = {
  chain: {
    rpc: import.meta.env.VITE_CHAIN_RPC,
    id: parseInt(import.meta.env.VITE_CHAIN_ID || "0"),
    hexId: "0x" + parseInt(import.meta.env.VITE_CHAIN_ID || "0").toString(16),
    network: import.meta.env.VITE_CHAIN_NETWORK,
    token: import.meta.env.VITE_CHAIN_TOKEN,
    name: import.meta.env.VITE_CHAIN_NAME,
    scan: import.meta.env.VITE_CHAIN_SCAN,
  },
  alchemy: {
    network: import.meta.env.VITE_ALCHEMY_NETWORK || undefined,
    apiKey: import.meta.env.VITE_ALCHEMY_API_KEY || undefined,
  },
  currency: {
    name: import.meta.env.VITE_CURRENCY_NAME || "USDC",
    address: import.meta.env.VITE_CURRENCY_ADDRESS || "",
    decimals: parseInt(import.meta.env.VITE_CURRENCY_DECIMALS || "6"),
  },
  faucet: {
    address: import.meta.env.VITE_FAUCET_ADDRESS,
    decimals: parseInt(import.meta.env.VITE_FAUCET_DECIMALS || "6"),
  },
  api: {
    url: import.meta.env.VITE_API_URL,
    clock: import.meta.env.VITE_API_CLOCK || 1000,
    clockCount: parseInt(import.meta.env.VITE_API_DEFAULT_CLOCK_COUNT || "600"),
    retry: {
      timeout: parseInt(import.meta.env.VITE_API_RETRY_TIMEOUT || "1500"),
      count: parseInt(import.meta.env.VITE_API_RETRY_COUNT || "10"),
    },
    defaultMaxAge: parseInt(import.meta.env.VITE_API_DEFAULT_MAX_AGE || "60000"),
  },
  environment: {
    name: import.meta.env.VITE_ENV_NAME,
    testnet: (import.meta.env.VITE_IS_TESTNET || "false") === "true",
    version: import.meta.env.VITE_ENV_VERSION || "development",
  },
  ethereum: {
    retry: {
      timeout: parseInt(import.meta.env.VITE_ETH_RETRY_TIMEOUT || "500"),
      count: parseInt(import.meta.env.VITE_ETH_RETRY_COUNT || "10"),
      transactCount: parseInt(import.meta.env.VITE_ETH_TRANSACT_COUNT || "300"),
    },
    clock: import.meta.env.VITE_ETH_CLOCK || 500,
    clockCount: parseInt(import.meta.env.VITE_ETH_DEFAULT_CLOCK_COUNT || "600"),
    defaultMaxAge: parseInt(import.meta.env.VITE_ETH_DEFAULT_MAX_AGE || "3000"),
  },
  refresh: {
    default: parseInt(import.meta.env.VITE_DEFAULT_REFRESH || "120000"),
    userInformation: parseInt(import.meta.env.VITE_REFRESH || "15000"),
  },
  pool: {
    address: import.meta.env.VITE_POOL_ADDRESS,
  },
  images: {
    defaultEtkLogo: "https://storage.googleapis.com/media-offchain-mumbai/media/etk_logos/default.png",
  },
  minicharts: {
    daysFrom: 15,
  },
  cashFlowCharts: {
    daysFrom: 30,
  },
  gas: {
    increase: 130, // 30%
  },
  rollbar: {
    accessToken: import.meta.env.VITE_ROLLBAR_ACCESS_TOKEN || "notoken",
    environment: import.meta.env.VITE_ROLLBAR_ENVIRONMENT,
    enabled: (import.meta.env.VITE_ROLLBAR_ENABLED || "false") === "true",
  },
  track: {
    name: "Track Wallet",
  },
  apy: {
    range: 30,
    maxAPY: 9.99, // 999%
  },
  apy360: {
    range: 360,
    maxAPY: 9.99, // 999%
  },
  walletConnect: {
    projectId: import.meta.env.VITE_WALLET_CONNECT_PROJECT_ID,
  },
  overrides: toList(import.meta.env.VITE_HAS_OVERRIDES),
  kyc: {
    risk: {
      url: import.meta.env.VITE_RISK_DISCLOSURE_URL,
      hash: import.meta.env.VITE_RISK_DISCLOSURE_HASH,
      last_updated: import.meta.env.VITE_RISK_DISCLOSURE_LAST_UPDATED,
    },
  },
  docs: {
    fees: {
      url: import.meta.env.VITE_DOC_FEES_URL,
    },
    eTokenRisks: {
      url: import.meta.env.VITE_DOC_ETOKEN_RISKS,
    },
  },
  kycIframe: {
    url: import.meta.env.VITE_KYC_IFRAME_URL,
    allowAnyOrigin: String(import.meta.env.VITE_ALLOW_ANY_ORIGIN || "false").toLowerCase() === "true",
  },
};

// Export named exports for destructuring imports
export const {
  chain,
  alchemy,
  currency,
  faucet,
  api,
  environment,
  ethereum,
  refresh,
  pool,
  images,
  minicharts,
  cashFlowCharts,
  gas,
  rollbar,
  track,
  apy,
  apy360,
  walletConnect,
  overrides,
  kyc,
  docs,
  kycIframe,
} = config;
